import React, { useState, useRef } from 'react';

// --- SVG ICONS (Matching Form Modal) ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"></path>
  </svg>
);

const PrintIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"></polyline>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
    <rect x="6" y="14" width="12" height="8"></rect>
  </svg>
);

const API_URL = '/api';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

const SurveyBOQViewModal = ({ open, onClose, data }) => {
  
  const [isMaximized, setIsMaximized] = useState(false);

  // --- RESIZABLE STATE & REFS ---
  const [columnWidths, setColumnWidths] = useState({
    itemNo: 120,
    type: 100,
    description: 280,
    projectQty: 220,
    surveyQty: 100,
    startDate: 140,
    endDate: 140,
    toIndent: 100,
    indented: 100,
    uom: 80,
    remark:280,
    activity: 200,       // <--- ADD THIS
    subActivity: 200
  });

  const resizingRef = useRef(null);

  // Ensure lines are parsed if they come as a string or nested
  const lines = Array.isArray(data?.Lines) 
    ? data.Lines 
    : (typeof data?.Lines === 'string' ? JSON.parse(data.Lines) : []);

  // Helper to format currency or numbers
  const formatNumber = (num) => {
    return num ? parseFloat(num).toFixed(6) : '0.00';
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // --- RESIZABLE HANDLERS ---
  const startResize = (columnKey, startX) => {
    resizingRef.current = {
      columnKey,
      startX,
      startWidth: columnWidths[columnKey]
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;

    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;

    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: Math.max(60, startWidth + delta)
    }));
  };

  const stopResize = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
    resizingRef.current = null;
  };

  // --- RESIZABLE TH COMPONENT ---
  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th
      style={{ width }}
      className={`relative p-2 ${align === 'right' ? 'text-right' : ''} select-none`}
    >
      {children}
      <div
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400"
        onMouseDown={(e) => onResize(e.clientX)}
      />
    </th>
  );

  if (!open || !data) return null;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .custom-scrollbar::-webkit-scrollbar { height: 8px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            
            .resizable-table {
              table-layout: fixed;
              min-width: max-content;
            }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${ isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl' }`} tabIndex={0}>
          
          {/* --- MODAL HEADER (Exact Copy of Form Design) --- */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-[#384959]">View Survey BOQ</h2>
                <p className="text-sm text-[#64748b] mt-1 font-medium">
                  No: <span className="text-[#384959]">{data.SurveyBOQNo || 'Draft'}</span>
                  {data.Status && (
                    <span className={`ml-3 px-2 py-0.5 rounded text-xs font-bold uppercase border ${
                      data.Status === 'Open' ? 'bg-green-100 text-green-700 border-green-200' :
                      data.Status === 'Released' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                      data.Status === 'Approved' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                      'bg-gray-100 text-gray-700 border-gray-200'
                    }`}>
                      {data.Status}
                    </span>
                  )}
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <button 
                  className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                  title="Print"
                  onClick={() => window.print()}
                >
                  <PrintIcon size={20} />
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* --- FORM BODY --- */}
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <div className="space-y-6 pb-6">
              
              {/* --- HEADER SECTION (Read Only) --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Survey BOQ Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  
                  {/* BOQ No */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">BOQ No</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {data.SurveyBOQNo || '-'}
                    </div>
                  </div>

                  {/* BOQ Date */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">BOQ Date</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {formatDate(data.BOQDate)}
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]"> Status</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 shadow-sm">
                      {data.ApprovalStatus === 'PENDING' ? (
                        <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                          Pending Approval (Level {data.CurrentLevel || 1})
                        </span>
                      ) : data.ApprovalStatus === 'REJECTED' ? (
                        <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                          Rejected
                        </span>
                      ) : data.ApprovalStatus === 'APPROVED' ? (
                        data.SendToBc === 1 || data.SendToBc === true ? (
                          <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
                            Sent to BC Successfully
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                            Approved (Ready to Send BC)
                          </span>
                        )
                      ) : (
                        <span className="text-xs text-gray-500 italic">
                          {data.SendToBc === 1 || data.SendToBc === true ? 'Sent to BC Successfully' : 'Not Submitted for Approval'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Site Engineer */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Site Engineer</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {data.SiteEngineerName || data.SiteEngineer || '-'}
                    </div>
                  </div>

                  {/* Project */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {data.ProjectCode || '-'}
                    </div>
                  </div>

                  {/* Feeder (Survey Location) */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Feeder (Survey Location)</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {data.FeederCode} - {data.FeederDescription}
                    </div>
                  </div>

                  {/* Project Description */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project Description</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {data.ProjectDescription || '-'}
                    </div>
                  </div>

                  {/* Project Manager */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project Manager</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm">
                      {data.ProjectManager || '-'}
                    </div>
                  </div>

                  {/* Location Fields */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {data.Circle || '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">District</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {data.District || '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Division</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {data.Division || '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {data.SubDivision || '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {data.SubStation || '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Parent Feeder</label>
                    <div className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#384959] shadow-sm uppercase">
                      {data.ParentFeederName 
                        ? `${data.ParentSiteCode} - ${data.ParentFeederName}` 
                        : data.ParentSiteCode || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* --- LINES SECTION (Read Only Table) --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">BOQ Lines</h3>
                  <div className="text-sm font-medium text-[#64748b]">
                    Count: {lines.length}
                  </div>
                </div>
                
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="resizable-table border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <ResizableTh width={columnWidths.itemNo} onResize={(x) => startResize('itemNo', x)}>
                          Item No
                        </ResizableTh>
                        <ResizableTh width={columnWidths.type} onResize={(x) => startResize('type', x)}>
                          Type
                        </ResizableTh>
                        <ResizableTh width={columnWidths.description} onResize={(x) => startResize('description', x)}>
                          Description
                        </ResizableTh>
                        <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)} align="center">
                          UOM
                        </ResizableTh>
                         <ResizableTh width={columnWidths.activity} onResize={(x) => startResize('activity', x)}>
                          Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.subActivity} onResize={(x) => startResize('subActivity', x)}>
                          Sub Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.projectQty} onResize={(x) => startResize('projectQty', x)} align="right">
                          Project Qty
                        </ResizableTh>
                        <ResizableTh width={columnWidths.surveyQty} onResize={(x) => startResize('surveyQty', x)} align="right">
                          Survey Qty
                        </ResizableTh>
                        <ResizableTh width={columnWidths.startDate} onResize={(x) => startResize('startDate', x)} align="right">
                          Start Date
                        </ResizableTh>
                        <ResizableTh width={columnWidths.endDate} onResize={(x) => startResize('endDate', x)} align="right">
                          End Date
                        </ResizableTh>
                        <ResizableTh width={columnWidths.toIndent} onResize={(x) => startResize('toIndent', x)} align="right">
                          To Indent
                        </ResizableTh>
                        <ResizableTh width={columnWidths.indented} onResize={(x) => startResize('indented', x)} align="right">
                          Indented
                        </ResizableTh>
                         <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('remark', x)}>
                        Remark
                      </ResizableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="p-8 text-center text-[#94A3B8]">
                            No line items found.
                          </td>
                        </tr>
                      ) : (
                        lines.map((line, idx) => (
                          <tr key={idx} className="border-t border-[#E2E8F0] hover:bg-[#F1F5F9]">
                            <td className="p-2 font-medium text-[#384959] uppercase" style={{ width: columnWidths.itemNo }}>
                              {line.ItemNo || '-'}
                            </td>
                            <td className="p-2 text-[#64748b]" style={{ width: columnWidths.type }}>
                              {line.PlanningLineType || '-'}
                            </td>
                            <td className="p-2 text-[#384959]" style={{ width: columnWidths.description }}>
                              {line.ItemDescription || '-'}
                            </td>
                            <td className="p-2 text-center text-[#64748b] uppercase" style={{ width: columnWidths.uom }}>
                              {line.UOM || '-'}
                            </td>
                             <td className="p-2 text-sm text-[#384959]" style={{ width: columnWidths.activity }}>
                              {line.ActivityCode && line.ActivityDescription
                                ? `${line.ActivityCode} - ${line.ActivityDescription}`
                                : (line.ActivityId || '-')}
                            </td>
                            <td className="p-2 text-sm text-[#384959]" style={{ width: columnWidths.subActivity }}>
                              {line.SubActivityCode && line.SubActivityDescription
                                ? `${line.SubActivityCode} - ${line.SubActivityDescription}`
                                : (line.SubActivityId || '-')}
                            </td>
                            <td className="p-2 text-right text-[#64748b]" style={{ width: columnWidths.projectQty }}>
                              {formatNumber(line.BOQQty)}
                            </td>
                            <td className="p-2 text-right font-medium text-[#384959]" style={{ width: columnWidths.surveyQty }}>
                              {formatNumber(line.SurveyQuantity)}
                            </td>
                            <td className="p-2 text-right font-medium text-[#384959]" style={{ width: columnWidths.startDate }}>
                              {formatDate(line.StartDate)}
                            </td>
                            <td className="p-2 text-right font-medium text-[#384959]" style={{ width: columnWidths.endDate }}>
                              {formatDate(line.EndDate)}
                            </td>
                            <td className="p-2 text-right font-bold text-blue-600" style={{ width: columnWidths.toIndent }}>
                              {formatNumber(line.QtyToIndent)}
                            </td>
                            <td className="p-2 text-right font-bold text-green-600" style={{ width: columnWidths.indented }}>
                              {formatNumber(line.TotalIndentQty)}
                            </td>
                            <td className="p-2  font-bold text-green-600" style={{ width: columnWidths.remark }}>
                              {line.Remark}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Summary Footer */}
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Lines</span>
                      <span className="text-base font-semibold">{lines.length}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Survey Quantity</span>
                      <span className="text-base font-semibold text-lg text-blue-600">
                        {formatNumber(lines.reduce((acc, line) => acc + (parseFloat(line.SurveyQuantity) || 0), 0))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- FOOTER --- */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button 
                type="button" 
                className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default SurveyBOQViewModal;